import React from 'react';
import { Routes, Route, useNavigate, Outlet, Navigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Box from '@mui/material/Box';
import { removeToken, isClient, isAuthenticated } from './auth';

// Импорт компонентов
import { LoginPage } from './components/LoginPage';
import { Navigation } from './components/Navigation';
import { RequireAuth, RequireAdmin, RequireAdminOrSales, RequireClientAuth } from './components/ProtectedRoute';
import { LeadsPage } from './components/LeadsPage';
import { ProjectsPage } from './components/ProjectsPage';
import { ProjectsDynamicTablePage } from './components/ProjectsDynamicTablePage';
import { UsersPage } from './components/UsersPage';
import { AnalyticsPage } from './components/AnalyticsPage';

// Импорт клиентских компонентов
import ClientLoginPage from './pages/ClientLoginPage';
import ClientDashboard from './pages/ClientDashboard';
import ClientLeadsPage from './pages/ClientLeadsPage';
import DisputesPage from './pages/DisputesPage';
import DisputeChatPage from './pages/DisputeChatPage';
import ClientCallCheckPage from './pages/ClientCallCheckPage';

// Импорт новых страниц
import { DuplicatesPage } from './pages/DuplicatesPage';
import { CallsPage } from './pages/CallsPage';
import { BonusPage } from './pages/BonusPage';
import QualityControlPage from './pages/QualityControlPage';
import { QualityControlV2Page } from './pages/QualityControlV2Page';
import ContactsEfficiencyPage from './pages/ContactsEfficiencyPage';
import { IntegrationsPage } from './components/IntegrationsPage';
import { FinancePage } from './components/FinancePage';
import { GoogleSheetsPage } from './components/GoogleSheetsPage';
import { OperatorReportPage } from './components/OperatorReportPage';
import { OperatorHoursPage } from './components/OperatorHoursPage';
import { EmployeeRolesPage } from './components/EmployeeRolesPage';
import { OperatorProjectMatrixPage } from './components/OperatorProjectMatrixPage';

function AdminPage() {
  const { t } = useTranslation();
  return <div>{t('admin')}</div>;
}

function App() {
  const navigate = useNavigate();

  const handleLogout = () => {
    removeToken();
    navigate('/login');
  };

  // Проверяем, является ли пользователь клиентом
  const isClientUser = isClient();
  const isAuthenticatedUser = isAuthenticated();

  return (
    <>
      <Routes>
        <Route path="/login" element={<LoginPage />} />
        <Route path="/client/login" element={<ClientLoginPage />} />
        
        {/* Редирект для неавторизованных пользователей на страницу логина */}
        <Route path="/" element={
          !isAuthenticatedUser ? <Navigate to="/login" replace /> :
          isClientUser ? <Navigate to="/client/dashboard" replace /> : 
          <Navigate to="/leads" replace />
        } />

        {/* Админские роуты с навигацией */}
        <Route element={<RequireAuth><Outlet /></RequireAuth>}>
          <Route path="/leads" element={
            <>
              <Navigation onLogout={handleLogout} />
              <Box sx={{ p: { xs: 1, sm: 2, md: 3 } }}>
                <LeadsPage />
              </Box>
            </>
          } />
          <Route path="/disputs" element={
            <>
              <Navigation onLogout={handleLogout} />
              <Box sx={{ p: { xs: 1, sm: 2, md: 3 } }}>
                <DisputesPage />
              </Box>
            </>
          } />
          <Route path="/disputs/:leadId" element={
            <>
              <Navigation onLogout={handleLogout} />
              <Box sx={{ p: { xs: 1, sm: 2, md: 3 } }}>
                <DisputeChatPage />
              </Box>
            </>
          } />
          <Route path="/projects" element={
            <>
              <Navigation onLogout={handleLogout} />
              <Box sx={{ p: { xs: 1, sm: 2, md: 3 } }}>
                <RequireAdmin><ProjectsPage /></RequireAdmin>
              </Box>
            </>
          } />
          <Route path="/projects/summary" element={
            <>
              <Navigation onLogout={handleLogout} />
              <Box sx={{ p: { xs: 1, sm: 2, md: 3 } }}>
                <RequireAdmin><ProjectsDynamicTablePage /></RequireAdmin>
              </Box>
            </>
          } />
          <Route path="/users" element={
            <>
              <Navigation onLogout={handleLogout} />
              <Box sx={{ p: { xs: 1, sm: 2, md: 3 } }}>
                <RequireAdmin><UsersPage /></RequireAdmin>
              </Box>
            </>
          } />
          <Route path="/analytics" element={
            <>
              <Navigation onLogout={handleLogout} />
              <Box sx={{ p: { xs: 1, sm: 2, md: 3 } }}>
                <RequireAdmin><AnalyticsPage /></RequireAdmin>
              </Box>
            </>
          } />
          <Route path="/admin" element={
            <>
              <Navigation onLogout={handleLogout} />
              <Box sx={{ p: { xs: 1, sm: 2, md: 3 } }}>
                <RequireAdmin><AdminPage /></RequireAdmin>
              </Box>
            </>
          } />
          <Route path="/duplicates" element={
            <>
              <Navigation onLogout={handleLogout} />
              <Box sx={{ p: { xs: 1, sm: 2, md: 3 } }}>
                <RequireAdmin><DuplicatesPage /></RequireAdmin>
              </Box>
            </>
          } />
          <Route path="/calls" element={
            <>
              <Navigation onLogout={handleLogout} />
              <Box sx={{ p: { xs: 1, sm: 2, md: 3 } }}>
                <RequireAdmin><CallsPage /></RequireAdmin>
              </Box>
            </>
          } />
          <Route path="/bonus" element={
            <>
              <Navigation onLogout={handleLogout} />
              <Box sx={{ p: { xs: 1, sm: 2, md: 3 } }}>
                <RequireAdmin><BonusPage /></RequireAdmin>
              </Box>
            </>
          } />
          <Route path="/quality-control" element={
            <>
              <Navigation onLogout={handleLogout} />
              <Box sx={{ p: { xs: 1, sm: 2, md: 3 } }}>
                <RequireAdmin><QualityControlPage /></RequireAdmin>
              </Box>
            </>
          } />
          <Route path="/quality-control-v2" element={
            <>
              <Navigation onLogout={handleLogout} />
              <Box sx={{ p: { xs: 1, sm: 2, md: 3 } }}>
                <RequireAdmin><QualityControlV2Page /></RequireAdmin>
              </Box>
            </>
          } />
          <Route path="/contacts-efficiency" element={
            <>
              <Navigation onLogout={handleLogout} />
              <Box sx={{ p: { xs: 1, sm: 2, md: 3 } }}>
                <RequireAdmin><ContactsEfficiencyPage /></RequireAdmin>
              </Box>
            </>
          } />
          <Route path="/integrations" element={
            <>
              <Navigation onLogout={handleLogout} />
              <Box sx={{ p: { xs: 1, sm: 2, md: 3 } }}>
                <RequireAdmin><IntegrationsPage /></RequireAdmin>
              </Box>
            </>
          } />
          <Route path="/finance" element={
            <>
              <Navigation onLogout={handleLogout} />
              <Box sx={{ p: { xs: 1, sm: 2, md: 3 } }}>
                <RequireAdminOrSales><FinancePage /></RequireAdminOrSales>
              </Box>
            </>
          } />
          <Route path="/google-sheets" element={
            <>
              <Navigation onLogout={handleLogout} />
              <Box sx={{ p: { xs: 1, sm: 2, md: 3 } }}>
                <RequireAdmin><GoogleSheetsPage /></RequireAdmin>
              </Box>
            </>
          } />
          <Route path="/operators-report" element={
            <>
              <Navigation onLogout={handleLogout} />
              <Box sx={{ p: { xs: 1, sm: 2, md: 3 } }}>
                <RequireAdmin><OperatorReportPage /></RequireAdmin>
              </Box>
            </>
          } />
          <Route path="/operator-hours" element={
            <>
              <Navigation onLogout={handleLogout} />
              <Box sx={{ p: { xs: 1, sm: 2, md: 3 } }}>
                <RequireAdmin><OperatorHoursPage /></RequireAdmin>
              </Box>
            </>
          } />
          <Route path="/operator-project-matrix" element={
            <>
              <Navigation onLogout={handleLogout} />
              <Box sx={{ p: { xs: 1, sm: 2, md: 3 } }}>
                <RequireAdmin><OperatorProjectMatrixPage /></RequireAdmin>
              </Box>
            </>
          } />
          <Route path="/employee-roles" element={
            <>
              <Navigation onLogout={handleLogout} />
              <Box sx={{ p: { xs: 1, sm: 2, md: 3 } }}>
                <RequireAdmin><EmployeeRolesPage /></RequireAdmin>
              </Box>
            </>
          } />
        </Route>

        {/* Клиентские роуты без навигации */}
        <Route element={<RequireClientAuth><Outlet /></RequireClientAuth>}>
          <Route path="/client/dashboard" element={<ClientDashboard />} />
          <Route path="/client/leads" element={<ClientLeadsPage />} />
          <Route path="/client/leads/:leadId" element={<ClientLeadsPage />} />
          <Route path="/client/disputs" element={<DisputesPage isClient />} />
          <Route path="/client/disputs/:leadId" element={<DisputeChatPage isClient />} />
          <Route path="/client/call-check" element={<ClientCallCheckPage />} />
        </Route>
      </Routes>
    </>
  );
}

export default App;
